import { SignedIn, SignedOut, UserButton } from "@clerk/clerk-react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Button } from "@the-drop/ui";
import { resolveClerkPublishableKey } from "./clerk-env";

const clerkPk = resolveClerkPublishableKey();

/**
 * Renders Clerk controls only when **`VITE_CLERK_PUBLISHABLE_KEY`** is set
 * (and **`ClerkProvider`** wraps the tree).
 *
 * Per redline 07: shell-level "Sign in" routes to the dedicated `/sign-in`
 * page (not a modal). The 7 in-flow auth requests across checkout / account
 * / tracking keep using `<SignInButton mode="modal">` to preserve task
 * state. `redirect_url` round-trips via the URL query string so post-
 * sign-in lands the user back where they started; Clerk's `<SignIn>`
 * picks it up natively.
 */
export function AuthHeader() {
  if (!clerkPk) {
    return (
      <span className="max-w-[14rem] text-right text-xs text-[var(--status-pending-text)]">
        Clerk: set{" "}
        <code className="rounded border border-[var(--card-border)] bg-[var(--card-bg)] px-1 font-mono text-2xs text-[var(--text-primary)]">
          VITE_CLERK_PUBLISHABLE_KEY
        </code>
      </span>
    );
  }

  return <AuthHeaderWithClerk />;
}

function AuthHeaderWithClerk() {
  /**
   * Skip self-referential redirect — when the user is already on /sign-in
   * or /sign-up, don't append `?redirect_url=/sign-in` (would loop on
   * post-auth navigation).
   */
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({ select: (s) => s.location.searchStr });
  const onAuthRoute = pathname.startsWith("/sign-in") || pathname.startsWith("/sign-up");
  const redirectUrl = onAuthRoute ? "" : pathname + (search ? `?${search}` : "");
  const linkSearch: { redirect_url?: string } = redirectUrl ? { redirect_url: redirectUrl } : {};

  return (
    <div className="flex items-center gap-2">
      <SignedOut>
        <Link to="/sign-in" search={linkSearch}>
          <Button type="button" variant="green" size="sm">
            Sign in
          </Button>
        </Link>
      </SignedOut>
      <SignedIn>
        <UserButton afterSignOutUrl="/" />
      </SignedIn>
    </div>
  );
}
